import { useEffect, useRef, type ReactNode } from 'react'

import { CloseIcon } from '@/components/ui/icons'
import { cn } from '@/lib/utils/cn'

import styles from './Modal.module.css'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  footer?: ReactNode
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  closeLabel?: string
  /** Hides the header's bottom divider line — off by default. */
  divider?: boolean
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
  size = 'md',
  closeLabel = 'Schließen',
  divider = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    if (!open) return

    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  return (
    <dialog
      ref={dialogRef}
      className={cn(styles.modal, size !== 'md' && styles[size])}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose()
      }}
    >
      <header className={cn(styles.header, !divider && styles.noDivider)}>
        <div>
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        <button type="button" className={styles.close} aria-label={closeLabel} onClick={onClose}>
          <CloseIcon />
        </button>
      </header>

      <div className={styles.body}>{children}</div>

      {footer && <footer className={styles.footer}>{footer}</footer>}
    </dialog>
  )
}
